/* One moveable list position: the block, and the insertion line above or below it. No grip since
 * 15 September 2026 (owner): the card's strips drag and its ⋯ starts the keyboard lift. */

.item {
  position: relative;
  list-style: none;
}

/* No divider between positions: the 02.08 gallery separates exercises with the gap between two
 * CARDS, each carrying its own hairline and shadow, rather than with a rule across a flat list. */

.row {
  display: flex;
  min-width: 0;
  border-radius: var(--radius-md);
}

/* The lift's host takes focus from the ⋯. While lifted the rail below says so; once the block has
 * dropped, focus is still here and the ring is what shows where the keys went. */
.row:focus-visible {
  outline: var(--border-focus) solid var(--text);
  outline-offset: var(--space-1px);
}

.body {
  flex: 1;
  min-width: 0;
}

/* ── The insertion indicator ────────────────────────────────────────────────────────────────
 * View ADR §11: "a thin accent line for an insertion point between siblings". Drawn as two
 * pseudo-elements on the item so nothing in the list moves — the ADR's next sentence is "content
 * never reflows under the pointer to preview a drop", and a real element inserted between rows
 * would push every row below it by its own height while the trainer is aiming at one of them.
 *
 * `Builder planu - tydzien - programator` draws the pair: a 3px rule inset 6px from each edge with
 * a 4px halo behind it, and a 9px dot at its left end. The halo is what keeps the rule readable
 * where it crosses a card's own hairline, and the dot is what makes a 3px line at the top of a long
 * list findable at a glance.
 *
 * The canon's rule is `--text`; this one is the ACCENT, which is view ADR §11's own wording and a
 * decision this file implements rather than re-opens. It is inside the scalpel budget for the
 * reason the week's target ring is: it exists for the length of one gesture, exactly one line is
 * painted at a time, and nothing accented is left on the surface the moment the drag ends.
 */
.item[data-edge]::after,
.item[data-pending]::after {
  position: absolute;
  right: var(--space-6px);
  left: var(--space-6px);
  height: var(--border-rail-thick);
  content: "";
  background: var(--accent);
  border-radius: var(--radius-bar);
  box-shadow: var(--ring-drop-line);
}

.item[data-edge]::before,
.item[data-pending]::before {
  position: absolute;
  left: var(--space-2px);
  z-index: 1;
  width: 9px;
  height: 9px;
  content: "";
  background: var(--accent);
  border-radius: var(--r-circle);
}

.item[data-edge="top"]::after,
.item[data-pending]::after {
  top: var(--space-minus-5px);
}

.item[data-edge="top"]::before,
.item[data-pending]::before {
  top: var(--space-minus-8px);
}

.item[data-edge="bottom"]::after {
  bottom: var(--space-minus-5px);
}

.item[data-edge="bottom"]::before {
  bottom: var(--space-minus-8px);
}

/* The source, while the block is in flight. Read off the STRIP the gesture started from rather than
 * held as state up here: a block has several strips — the head, the `Grupa` bar, both heads of a
 * superset — and only the one being carried knows it is being carried. `:has()` is already this
 * slice's way of reading a descendant's state (`SetTable`'s focused cell). Dimmed rather than hidden: unlike the week card, this
 * block sits in a vertical list where removing it would collapse the rows below it under the
 * pointer — the reflow the ADR forbids. `opacity` is safe here because nothing inside is being
 * READ during the gesture; the block is the thing being carried, not an explanation. */
.row:has([data-dragging]) {
  opacity: var(--drag-source-opacity);
}

/* Lifted by keyboard: a neutral rail down the left, in the same place the canon marks a selected
 * exercise (`.xb.is-sel { box-shadow: inset 3px 0 0 var(--text) }`). Neutral on purpose — the accent
 * belongs to the target line, and two accents for one gesture is the budget gone. */
.row[data-lifted] {
  box-shadow: var(--ring-lifted);
}
